import React from 'react';
import { Card, CardContent, Box, Typography, Divider, Chip, LinearProgress, Tooltip } from '@mui/material';
import { CheckCircle, Flag, Chat, MenuBook } from '@mui/icons-material';

function getCapacityColor(utilization) {
  if (utilization > 95) return 'error';
  if (utilization >= 80) return 'warning';
  return 'success';
}

function StatPill({ icon: Icon, value, label, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Icon sx={{ fontSize: 14, color }} />
      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.825rem' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function SummaryStatsBar({
  weeklyProgress,
  alerts,
  engagement,
  caseload,
  supervisorNotes,
  consultationsThisMonth,
}) {
  const { active7d, declining, disengaged } = { ...(engagement || {}) };
  const { total, capacity, utilization } = caseload || {};

  const checkIns = weeklyProgress?.checkIns ?? 0;
  const goals = weeklyProgress?.goalsCompleted ?? 0;
  const conversations = weeklyProgress?.conversations?.total ?? 0;
  const journals = weeklyProgress?.journals ?? 0;

  return (
    <Card>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

        {/* Section: This Week */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5 }}>
            This Week
          </Typography>
          <StatPill icon={CheckCircle} value={checkIns} label="check-ins" color="#4caf50" />
          <Typography variant="body2" color="text.disabled">·</Typography>
          <StatPill icon={Flag} value={goals} label="goals" color="#2196f3" />
          <Typography variant="body2" color="text.disabled">·</Typography>
          <StatPill icon={Chat} value={conversations} label="convos" color="#9c27b0" />
          <Typography variant="body2" color="text.disabled">·</Typography>
          <StatPill icon={MenuBook} value={journals} label="journals" color="#009688" />
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Section: Engagement */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mr: 0.5, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.5 }}>
            Engagement
          </Typography>
          <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 500, fontSize: '0.825rem' }}>
            {active7d ?? 0} active
          </Typography>
          <Typography variant="body2" color="text.disabled">·</Typography>
          <Typography variant="body2" sx={{ color: '#ed6c02', fontWeight: 500, fontSize: '0.825rem' }}>
            {declining ?? 0} declining
          </Typography>
          <Typography variant="body2" color="text.disabled">·</Typography>
          <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 500, fontSize: '0.825rem' }}>
            {disengaged ?? 0} disengaged
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Section: Capacity */}
        <Tooltip title={`${total ?? 0} of ${capacity ?? 0} capacity`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(utilization ?? 0, 100)}
              color={getCapacityColor(utilization ?? 0)}
              sx={{ height: 6, borderRadius: 3, flex: 1 }}
            />
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ whiteSpace: 'nowrap' }}>
              {utilization ?? 0}%
            </Typography>
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* Section: Alerts + Notes */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {(alerts?.pending ?? 0) > 0 && (
            <Chip
              label={`${alerts.pending} alert${alerts.pending !== 1 ? 's' : ''}`}
              size="small"
              color="warning"
              sx={{ fontWeight: 500, height: 22, fontSize: '0.7rem' }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {supervisorNotes ?? 0} notes · {consultationsThisMonth ?? 0} consults
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
